import { useMemo } from "react";

import { api, type ApiError } from "./client";
import { useResource } from "./useResource";

export type DiffSide = "new" | "old";

// GET /api/v1/tasks/:id/diff-comments
export type ApiDiffComment = {
  readonly author_public_id: string | null;
  readonly avatar_initials: string | null;
  readonly body: string;
  readonly display_name: string | null;
  readonly line: number;
  readonly occurred_at: string;
  // True when the file moved since this comment was anchored (§Diff comments).
  readonly outdated: boolean;
  // The parent comment's public id when this is a threaded reply, else null.
  readonly parent_public_id: string | null;
  readonly path: string;
  readonly public_id: string;
  readonly side: DiffSide;
  readonly status: string;
};

export type UseDiffComments = {
  readonly comments: ReadonlyArray<ApiDiffComment>;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
};

export const useDiffComments = (taskId: string | null): UseDiffComments => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiDiffComment>
  >(taskId ? `/tasks/${taskId}/diff-comments` : null);
  const comments = useMemo(() => data ?? [], [data]);
  return { comments, error, loading, refetch };
};

export type AddDiffCommentInput = {
  readonly body: string;
  readonly line: number;
  // Set to a parent comment's public id to post a threaded reply.
  readonly parentCommentId?: string;
  readonly path: string;
  readonly side: DiffSide;
  readonly snapshotId: string;
};

export const addDiffComment = (
  taskId: string,
  input: AddDiffCommentInput,
): Promise<unknown> =>
  api.post(`/tasks/${taskId}/diff-comments`, {
    body: input.body,
    line: input.line,
    parent_comment_id: input.parentCommentId,
    path: input.path,
    side: input.side,
    snapshot_public_id: input.snapshotId,
  });

export type PatchDiffCommentInput = {
  readonly body?: string;
  readonly status?: "published" | "resolved";
};

// PATCH — edit the body (author only) or resolve.
export const patchDiffComment = (
  taskId: string,
  commentId: string,
  input: PatchDiffCommentInput,
): Promise<unknown> =>
  api.patch(`/tasks/${taskId}/diff-comments/${commentId}`, input);

export const deleteDiffComment = (
  taskId: string,
  commentId: string,
): Promise<unknown> => api.del(`/tasks/${taskId}/diff-comments/${commentId}`);
